import {
  DssAnalyticsContext,
  GameHighlights,
  HeroSection,
  LanguageContext,
  PlayerPerformance,
  TransactionsModal,
  captureException,
} from '@dapperlabs/core-fe';
import i18next from 'i18next';
import { GetServerSidePropsContext } from 'next';
import { useMemo, useState } from 'react';
import { useSession } from 'src/edge/session';
import Head from 'src/general/components/Head/Head';
import { MomentCirculation } from 'src/general/components/MomentCirculation';
import { SalesHistory } from 'src/general/components/SalesHistory';
import { useTransactionsModal } from 'src/general/hooks';
import { useMomentAnalytics } from 'src/general/hooks/useMomentAnalytics';
import { getMomentData } from 'src/general/utils/moments';
import { addApolloState, createApolloClient } from 'src/lib/apollo';
import { useFeatureFlags } from 'src/lib/launchDarkly';
import { useTranslation } from 'src/lib/translations';
import {
  GET_PLAY,
  SEARCH_AGGREGATION,
  searchAggregationByEditionID,
  searchGolazosNftByID,
} from 'src/modules/Queries';
import {
  searchEditionAggregationData,
  searchEditionSalesHistory,
} from 'src/modules/Queries/editionQueries';
import {
  GetPlayQuery,
  GetPlayQueryVariables,
} from 'src/modules/Queries/golazo/getPlay.generated';
import {
  SearchGolazosNftAggregationQuery,
  SearchGolazosNftAggregationQueryVariables,
} from 'src/modules/Queries/searchAggregation.generated';
import { SEARCH_GOLAZOS_NFT } from 'src/modules/Queries/searchNft';
import { NonEmptyString } from 'src/modules/Queries/utilTypes';
import { Box, Flex, breakpoints, useMediaQuery } from 'src/theme';

import LaligaColorLogo from './../../modules/Layout/assets/png/laliga_logo_color.png';

type MomentProps = {
  aggregationData: {
    forSaleCount: number;
    hiddenCount: number;
    ownedCount: number;
    reservedCount: number;
  };
  aggregationPromise: any;
  merchandising: any;
  momentID: string;
  momentNFT: any;
  momentPrice: any;
  play: any;
  playDataID: string;
  price: any;
  recentSales: any;
  salesHistory: {
    latestSales: any;
    topSales: any;
  };
};
const Moment = ({
  momentNFT,
  play,
  playDataID,
  aggregationData,
  salesHistory,
  momentPrice,
}: MomentProps) => {
  const { isMarketplaceEnabled, isAllowedFullPlay } = useFeatureFlags();
  const { t } = useTranslation();
  const languages = useMemo(
    () => [
      { label: 'English', urlValue: '_default', value: 'en' },
      { label: 'Español', urlValue: '_es', value: 'es' },
    ],
    [],
  );

  const [isMdUp] = useMediaQuery(`screen and (min-width: ${breakpoints.md})`);

  const { state: sessionState } = useSession();
  const flowAddress = useMemo(
    () => sessionState.context?.profile?.flowAddress?.replace(/0x/g, ''),
    [sessionState.context.profile],
  );
  const isLoggedIn = useMemo(() => Boolean(flowAddress), [flowAddress]);

  const moment = getMomentData({
    aggregationData,
    languages,
    moment: momentNFT,
    play: play?.getPlay?.play,
    playDataID,
    salesHistory,
    sessionState,
    showMobileVideos: !isMdUp,
    t,
  });

  const metadata = moment.moment.type.split(', ');

  const momentInfo = {
    amountMomentsBurned: moment.burnedSize,
    currentPosition: moment.moment.serialNumber,
    nameFootballer: moment.moment.title,
    rarity: moment.rarityType,
    tags: metadata,
    totalAmountMoments: moment.editionSize,
  };

  const analytics = useMomentAnalytics(momentNFT);

  const [videoLanguage, setVideoLanguage] = useState(i18next.language);
  const [globalLanguage, setGlobalLanguage] = useState(i18next.language);

  i18next.on('languageChanged', (lang) => {
    setVideoLanguage(lang);
    setGlobalLanguage(lang);
  });

  const handleLanguage = useMemo(
    () => ({
      languages,
      onChangeLanguage: ({ value }) => setVideoLanguage(value),
      selectedLanguage: languages.find((lang) => lang.value === videoLanguage),
    }),
    [videoLanguage, languages],
  );

  const isOwnerNFT = useMemo(
    () => flowAddress === momentNFT.owner_address,
    [flowAddress, momentNFT.owner_address],
  );

  const {
    state,
    setState,
    stage,
    setStage,
    typeModal,
    transactionStage,
    txId,
    setTransactionStage,
    additionalButtonHandler,
    heroSectionButton,
    prices,
    submitModal,
    isOpen,
    setIsOpen,
  } = useTransactionsModal({
    flowAddress,
    imageUrl: moment.carouselItems[videoLanguage]?.[1]?.src,
    isLoggedIn,
    isOwnerNFT,
    momentNFT,
    nftID: momentNFT.id,
    price: momentPrice,
  });

  return (
    <>
      <Head
        description={t('Head.MomentPage.Description', {
          playDescription: moment.summary[globalLanguage],
        })}
        openGraphImage={moment.heroImage[globalLanguage]}
        subtitle={t('Head.MomentPage.Subtitle', {
          playType: momentInfo.tags[0],
          playerName: momentInfo.nameFootballer,
        })}
      />
      <DssAnalyticsContext.Provider value={analytics}>
        <TransactionsModal
          additionalButtonHandler={additionalButtonHandler}
          imageSrc={moment.carouselItems[videoLanguage]?.[1]?.src}
          isOpen={isOpen}
          momentInfo={momentInfo}
          prices={prices}
          setIsOpen={setIsOpen}
          setStage={setStage}
          setState={setState}
          setTransactionStage={setTransactionStage}
          stage={stage}
          state={state}
          submitModal={submitModal}
          transactionStage={transactionStage}
          txId={txId}
          typeModal={typeModal}
        />
        <Flex justifyContent='center' paddingX='1rem'>
          <Box maxWidth='1224px' paddingY='2rem'>
            <LanguageContext.Provider value={handleLanguage}>
              <HeroSection
                avgPrice={momentPrice?.avg}
                burnedSize={moment.burnedSize}
                buttonLabel={
                  typeof heroSectionButton !== 'boolean'
                    ? heroSectionButton?.label
                    : ''
                }
                carouselItems={moment.carouselItems[videoLanguage]}
                countForSale={momentPrice?.count}
                currency={{ suffix: 'USD' }}
                editionSize={moment.editionSize}
                isAuthorized={isLoggedIn}
                isDisableButton={!isAllowedFullPlay}
                isShowBtn={isMarketplaceEnabled && Boolean(heroSectionButton)}
                lowestPrice={momentPrice?.min}
                moment={moment.moment}
                onClick={
                  typeof heroSectionButton !== 'boolean'
                    ? heroSectionButton?.callback
                    : () => null
                }
                rarityType={moment.rarityType}
                summary={moment.summary[globalLanguage]}
                totalBurned={moment.totalBurned}
              />
            </LanguageContext.Provider>
          </Box>
        </Flex>

        <Box bgColor='rgba(23,23,21,0.4)' paddingX='1rem'>
          <Box maxWidth='1224px' margin='0 auto' paddingY='3.75rem'>
            <GameHighlights
              duration={90}
              logo={LaligaColorLogo}
              playData={moment}
            />
            <PlayerPerformance
              paddingTop={{ base: '3rem', sm: '5rem' }}
              paddingBottom='3rem'
              player={moment}
              season={moment.season}
            />
            <MomentCirculation
              sx={{
                borderTop: '1px solid #2B2C33',
                margin: '0 auto',
                maxWidth: '1224px',
                paddingY: '6.5rem',
              }}
              circulationId='moment-details'
              {...moment}
            />
            <SalesHistory
              editionId={moment.editionId}
              latestSales={moment.latestSales}
              topSales={moment.topSales}
            />
          </Box>
        </Box>
      </DssAnalyticsContext.Provider>
    </>
  );
};

const MomentPresenter = ({
  error,
  ...momentProps
}: MomentProps & { error?: boolean }) => {
  if (error) return <>Unable to load moment details.</>;
  return <Moment {...momentProps} />;
};
// Fetch moment details info from the server to render metadata
export async function getServerSideProps({
  req,
  res,
  query,
}: GetServerSidePropsContext) {
  try {
    const client = createApolloClient();
    const { id: momentID } = query;

    const momentNFTQuery = await client.query({
      context: {
        capturePolicy: 'none',
        clientName: 'platformAPI',
        req,
      },
      query: SEARCH_GOLAZOS_NFT(),
      variables: searchGolazosNftByID(momentID as string),
    });

    if (momentNFTQuery.errors) throw momentNFTQuery.errors;

    const momentNFT = momentNFTQuery?.data?.searchGolazosNft?.edges?.[0]?.node;

    const playDataID = momentNFT?.edition?.play?.metadata?.play_data_id;
    const momentEditionID = momentNFT?.edition?.id;

    if (!playDataID || !momentEditionID) {
      throw new Error('Insufficient data for further queries');
    }
    const aggregationQuery = await client.query<
      SearchGolazosNftAggregationQuery,
      SearchGolazosNftAggregationQueryVariables
    >({
      context: {
        clientName: 'platformAPI',
        req,
      },
      query: SEARCH_AGGREGATION,
      variables: searchAggregationByEditionID(
        momentEditionID as NonEmptyString<string>,
      ),
    });

    if (aggregationQuery.errors) throw momentNFTQuery.errors;

    const aggregationQueries = searchEditionAggregationData(
      client,
      momentEditionID as NonEmptyString<string>,
    );

    const salesHistoryQueries = searchEditionSalesHistory(
      client,
      momentEditionID as NonEmptyString<string>,
    );

    const [
      play,
      hiddenMoments,
      reservedMoments,
      ownedMoments,
      forSaleMoments,
      topSales,
      latestSales,
    ] = await Promise.all([
      client.query<GetPlayQuery, GetPlayQueryVariables>({
        context: {
          capturePolicy: 'none',
          clientName: 'laligaAPI',
          req,
        },
        query: GET_PLAY,
        variables: { playID: playDataID },
      }),
      aggregationQueries.hiddenCount,
      aggregationQueries.reservedCount,
      aggregationQueries.ownedCount,
      aggregationQueries.forSaleCount,
      salesHistoryQueries.topSales,
      salesHistoryQueries.latestSales,
    ]);

    if (play.errors) throw play.errors;
    if (hiddenMoments.errors) throw hiddenMoments.errors;
    if (reservedMoments.errors) throw reservedMoments.errors;
    if (ownedMoments.errors) throw ownedMoments.errors;
    if (forSaleMoments.errors) throw forSaleMoments.errors;
    if (latestSales.errors) throw latestSales.errors;
    if (topSales.errors) throw topSales.errors;

    const DENOMINATOR = 100000000;

    const historySales =
      latestSales?.data?.searchGolazosMarketplaceHistory?.edges || [];

    const totalSales = historySales.length;

    const sum = historySales?.reduce((acc, curr) => {
      acc += parseInt(curr.node.price);
      return acc;
    }, 0);

    const avgPrice = totalSales > 0 ? sum / totalSales / DENOMINATOR : 0;

    const momentPrice = {
      avg: avgPrice,
      count:
        forSaleMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node?.id
          ?.count || 0,
      max:
        topSales?.data?.searchGolazosMarketplaceHistory?.edges?.[0]?.node
          ?.price / DENOMINATOR || 0,
      min:
        aggregationQuery?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
          ?.listing?.price?.min / DENOMINATOR || 0,
    };

    return addApolloState(client, {
      props: {
        aggregationData: {
          forSaleCount:
            forSaleMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
              ?.id?.count || 0,
          hiddenCount:
            hiddenMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
              ?.id?.count || 0,
          ownedCount:
            ownedMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
              ?.id?.count || 0,
          reservedCount:
            reservedMoments?.data?.searchGolazosNftAggregation?.edges?.[0]?.node
              ?.id?.count || 0,
        },
        momentID,
        momentNFT,
        momentPrice: momentPrice ?? null,
        play: play?.data,
        playDataID,
        salesHistory: {
          latestSales: {
            count:
              latestSales?.data?.searchGolazosMarketplaceHistory?.totalCount ||
              0,
            data:
              latestSales?.data?.searchGolazosMarketplaceHistory?.edges || [],
          },
          topSales: {
            count:
              topSales?.data?.searchGolazosMarketplaceHistory?.totalCount || 0,
            data: topSales?.data?.searchGolazosMarketplaceHistory?.edges || [],
          },
        },
      },
    });
  } catch (error) {
    captureException(error, { req, res });
    return { props: { error: true } };
  }
}

export default MomentPresenter;
